import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Combobox } from '../components/ui/combobox';
import { toast } from 'sonner';
import {
  Stethoscope,
  Heart,
  Plus,
  Copy,
  Save,
  Search,
  Trash2,
  AlertTriangle,
  Clock,
  User,
  Calendar,
  FileText,
  Pill,
  Package,
  Briefcase,
  History,
  ChevronDown,
  ChevronUp,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

// Frequency options
const FREQUENCIA_OPTIONS = [
  { value: '1x ao dia', label: '1x ao dia' },
  { value: '2x ao dia', label: '2x ao dia (12/12h)' },
  { value: '3x ao dia', label: '3x ao dia (8/8h)' },
  { value: '4x ao dia', label: '4x ao dia (6/6h)' },
  { value: '6x ao dia', label: '6x ao dia (4/4h)' },
  { value: '1x por semana', label: '1x por semana' },
  { value: '2x por semana', label: '2x por semana' },
  { value: '3x por semana', label: '3x por semana' },
  { value: 'dias alternados', label: 'Dias alternados' },
  { value: 'se necessário', label: 'Se necessário (SN)' },
  { value: 'conforme orientação', label: 'Conforme orientação' },
];

const Prescricao = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // State
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('medica');
  const [selectedInternacao, setSelectedInternacao] = useState(null);
  const [internacoes, setInternacoes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Medical prescription state
  const [prescricaoMedica, setPrescricaoMedica] = useState(null);
  const [historicoPrescricoesMedicas, setHistoricoPrescricoesMedicas] = useState([]);
  const [showHistoricoMedica, setShowHistoricoMedica] = useState(false);

  // Nursing prescription state
  const [prescricaoEnfermagem, setPrescricaoEnfermagem] = useState(null);
  const [historicoPrescricoesEnfermagem, setHistoricoPrescricoesEnfermagem] = useState([]);
  const [showHistoricoEnfermagem, setShowHistoricoEnfermagem] = useState(false);

  // Evolutions
  const [evolucoesMedicas, setEvolucoesMedicas] = useState([]);
  const [evolucoesEnfermagem, setEvolucoesEnfermagem] = useState([]);

  // Form state
  const [formMedica, setFormMedica] = useState({
    orientacoes: '',
    evolucao: '',
    itens: [],
  });
  const [formEnfermagem, setFormEnfermagem] = useState({
    evolucao: '',
    materiais: [],
  });

  // Stock and services data
  const [estoqueItens, setEstoqueItens] = useState([]);
  const [servicosCatalogo, setServicosCatalogo] = useState([]);

  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [showCreateNonStockModal, setShowCreateNonStockModal] = useState(false);
  const [nonStockItemName, setNonStockItemName] = useState('');

  // Alerts
  const [alertasVencidas, setAlertasVencidas] = useState([]);

  // Permissions
  const canEditMedica = ['admin', 'master_admin', 'medico', 'médico', 'doctor'].includes(user?.perfil?.toLowerCase());
  const canEditEnfermagem = ['admin', 'master_admin', 'enfermeiro', 'enfermeira', 'nurse', 'tecnico_enfermagem', 'técnico_enfermagem'].includes(user?.perfil?.toLowerCase());

  // Fetch internações ativas (excluding alta_efetivada via ativas=true)
  const fetchInternacoes = useCallback(async () => {
    try {
      const response = await api.get('/internacoes?ativas=true&limit=500');
      setInternacoes(response.data || []);
    } catch (error) {
      console.error('Error fetching internacoes:', error);
    }
  }, [api]);

  // Fetch stock items
  const fetchEstoque = useCallback(async () => {
    try {
      const response = await api.get('/estoque/itens?limit=1000');
      setEstoqueItens(response.data?.itens || response.data || []);
    } catch (error) {
      console.error('Error fetching estoque:', error);
    }
  }, [api]);

  // Fetch services catalog
  const fetchServicos = useCallback(async () => {
    try {
      const response = await api.get('/servicos/catalogo');
      setServicosCatalogo(response.data || []);
    } catch (error) {
      console.error('Error fetching servicos:', error);
    }
  }, [api]);

  // Fetch prescription data for selected internação
  const fetchPrescricaoData = useCallback(async (internacaoId) => {
    if (!internacaoId) return;
    
    setLoading(true);
    try {
      // Fetch medical prescription
      const [medicaRes, enfermagemRes, evolMedicaRes, evolEnfRes, alertasRes] = await Promise.all([
        api.get(`/prescricao/medica/${internacaoId}`),
        api.get(`/prescricao/enfermagem/${internacaoId}`),
        api.get(`/prescricao/evolucoes/${internacaoId}?tipo=Medica&limit=20`),
        api.get(`/prescricao/evolucoes/${internacaoId}?tipo=Enfermagem&limit=20`),
        api.get(`/prescricao/alertas-vencidas/${internacaoId}`),
      ]);

      setPrescricaoMedica(medicaRes.data?.ativa || null);
      setHistoricoPrescricoesMedicas(medicaRes.data?.prescricoes || []);
      
      setPrescricaoEnfermagem(enfermagemRes.data?.ativa || null);
      setHistoricoPrescricoesEnfermagem(enfermagemRes.data?.prescricoes || []);
      
      setEvolucoesMedicas(evolMedicaRes.data?.evolucoes || []);
      setEvolucoesEnfermagem(evolEnfRes.data?.evolucoes || []);
      
      setAlertasVencidas(alertasRes.data?.alerts || []);

      // Initialize forms with active prescription data
      if (medicaRes.data?.ativa) {
        setFormMedica({
          orientacoes: medicaRes.data.ativa.orientacoes || '',
          evolucao: '',
          itens: medicaRes.data.ativa.itens || [],
        });
      } else {
        // Load default services
        const defaultRes = await api.get('/prescricao/servicos-padrao');
        const defaultItens = (defaultRes.data?.servicos || []).map(s => ({
          tipo: 'Servico',
          ref_id: s.id,
          nome_livre: null,
          nome_ref: s.nome,
          dose: null,
          unidade: null,
          frequencia: s.frequencia_padrao,
          observacoes: '',
        }));
        setFormMedica({
          orientacoes: '',
          evolucao: '',
          itens: defaultItens,
        });
      }

      if (enfermagemRes.data?.ativa) {
        setFormEnfermagem({
          evolucao: '',
          materiais: enfermagemRes.data.ativa.materiais || [],
        });
      } else {
        setFormEnfermagem({
          evolucao: '',
          materiais: [],
        });
      }

    } catch (error) {
      console.error('Error fetching prescription data:', error);
      toast.error('Erro ao carregar dados da prescrição');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Initial load
  useEffect(() => {
    fetchInternacoes();
    fetchEstoque();
    fetchServicos();
  }, [fetchInternacoes, fetchEstoque, fetchServicos]);

  // Check URL params for internação
  useEffect(() => {
    const internacaoId = searchParams.get('internacao');
    if (internacaoId && internacoes.length > 0) {
      const found = internacoes.find(i => i.id === internacaoId);
      if (found) {
        setSelectedInternacao(found);
        fetchPrescricaoData(internacaoId);
      }
    }
  }, [searchParams, internacoes, fetchPrescricaoData]);

  // Handle internação selection
  const handleSelectInternacao = (internacao) => {
    setSelectedInternacao(internacao);
    navigate(`/prescricao?internacao=${internacao.id}`);
    fetchPrescricaoData(internacao.id);
  };

  // Filter internações by search
  const filteredInternacoes = internacoes.filter(i => 
    i.paciente_nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Save medical prescription
  const handleSaveMedica = async () => {
    if (!selectedInternacao) {
      toast.error('Selecione um paciente primeiro');
      return;
    }

    if (!canEditMedica) {
      toast.error('Sem permissão para criar prescrição médica');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        internacao_id: selectedInternacao.id,
        orientacoes: formMedica.orientacoes,
        evolucao: formMedica.evolucao,
        itens: formMedica.itens,
      };

      const response = await api.post('/prescricao/medica', payload);
      toast.success(response.data?.message || 'Prescrição médica salva com sucesso');
      
      // Show additional toast for auto-sync
      if (formMedica.itens.length > 0) {
        setTimeout(() => {
          toast.info('Consumo atualizado automaticamente a partir da prescrição.', {
            duration: 4000
          });
        }, 500);
      }
      
      fetchPrescricaoData(selectedInternacao.id);
    } catch (error) {
      console.error('Error saving medical prescription:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar prescrição médica');
    } finally {
      setLoading(false);
    }
  };

  // Save nursing prescription
  const handleSaveEnfermagem = async () => {
    if (!selectedInternacao) {
      toast.error('Selecione um paciente primeiro');
      return;
    }

    if (!canEditEnfermagem) {
      toast.error('Sem permissão para criar prescrição de enfermagem');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        internacao_id: selectedInternacao.id,
        evolucao: formEnfermagem.evolucao,
        materiais: formEnfermagem.materiais,
      };

      const response = await api.post('/prescricao/enfermagem', payload);
      toast.success(response.data?.message || 'Prescrição de enfermagem salva com sucesso');
      
      // Show additional toast for auto-sync
      if (formEnfermagem.materiais.length > 0) {
        setTimeout(() => {
          toast.info('Consumo atualizado automaticamente a partir da prescrição.', {
            duration: 4000
          });
        }, 500);
      }
      
      fetchPrescricaoData(selectedInternacao.id);
    } catch (error) {
      console.error('Error saving nursing prescription:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar prescrição de enfermagem');
    } finally {
      setLoading(false);
    }
  };

  // Copy prescription
  const handleCopyPrescricao = async (prescricaoId, tipo) => {
    try {
      const endpoint = tipo === 'medica' 
        ? `/prescricao/medica/${prescricaoId}/copiar`
        : `/prescricao/enfermagem/${prescricaoId}/copiar`;
      
      const response = await api.get(endpoint);
      
      if (tipo === 'medica') {
        setFormMedica(prev => ({
          ...prev,
          orientacoes: response.data.orientacoes || '',
          itens: response.data.itens || [],
        }));
        toast.success('Prescrição copiada. Edite e salve.');
      } else {
        setFormEnfermagem(prev => ({
          ...prev,
          materiais: response.data.materiais || [],
        }));
        toast.success('Prescrição copiada. Edite e salve.');
      }
    } catch (error) {
      toast.error('Erro ao copiar prescrição');
    }
  };

  // Copy evolution
  const handleCopyEvolucao = async (evolucaoId, tipo) => {
    try {
      const response = await api.get(`/prescricao/evolucoes/${evolucaoId}/copiar`);
      
      if (tipo === 'Medica') {
        setFormMedica(prev => ({
          ...prev,
          evolucao: response.data.texto || '',
        }));
      } else {
        setFormEnfermagem(prev => ({
          ...prev,
          evolucao: response.data.texto || '',
        }));
      }
      toast.success('Evolução copiada');
    } catch (error) {
      toast.error('Erro ao copiar evolução');
    }
  };

  // Add item to medical prescription
  const [newItem, setNewItem] = useState({
    tipo: 'MedicamentoEstoque',
    ref_id: '',
    nome_livre: '',
    dose: '',
    unidade: '',
    frequencia: '1x ao dia',
    observacoes: '',
  });

  const handleAddItem = () => {
    const item = { ...newItem };
    
    if (item.tipo === 'MedicamentoEstoque' || item.tipo === 'MaterialEstoque') {
      const estoqueItem = estoqueItens.find(e => e.id === item.ref_id);
      if (estoqueItem) {
        item.nome_ref = estoqueItem.nome;
      }
    } else if (item.tipo === 'Servico') {
      const servico = servicosCatalogo.find(s => s.id === item.ref_id);
      if (servico) {
        item.nome_ref = servico.nome;
      }
    }

    setFormMedica(prev => ({
      ...prev,
      itens: [...prev.itens, item],
    }));
    
    setNewItem({
      tipo: 'MedicamentoEstoque',
      ref_id: '',
      nome_livre: '',
      dose: '',
      unidade: '',
      frequencia: '1x ao dia',
      observacoes: '',
    });
    setShowAddItemModal(false);
  };

  // Add material to nursing prescription
  const [newMaterial, setNewMaterial] = useState({
    estoque_item_id: '',
    nome: '',
    quantidade_por_uso: 1,
    unidade: 'un',
    frequencia: '1x ao dia',
    recorrente: true,
    por_diaria: false,
    observacoes: '',
  });

  const handleAddMaterial = () => {
    const mat = { ...newMaterial };
    const estoqueItem = estoqueItens.find(e => e.id === mat.estoque_item_id);
    if (estoqueItem) {
      mat.nome = estoqueItem.nome;
    }

    setFormEnfermagem(prev => ({
      ...prev,
      materiais: [...prev.materiais, mat],
    }));
    
    setNewMaterial({
      estoque_item_id: '',
      nome: '',
      quantidade_por_uso: 1,
      unidade: 'un',
      frequencia: '1x ao dia',
      recorrente: true,
      por_diaria: false,
      observacoes: '',
    });
    setShowAddMaterialModal(false);
  };

  // Remove item
  const handleRemoveItem = (index) => {
    setFormMedica(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index),
    }));
  };

  // Remove material
  const handleRemoveMaterial = (index) => {
    setFormEnfermagem(prev => ({
      ...prev,
      materiais: prev.materiais.filter((_, i) => i !== index),
    }));
  };

  // Update item frequency
  const handleUpdateItemFrequencia = (index, frequencia) => {
    setFormMedica(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => 
        i === index ? { ...item, frequencia } : item
      ),
    }));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="prescricao-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Stethoscope className="h-8 w-8 text-blue-600" />
              Prescrição
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerenciamento de prescrições médicas e de enfermagem
            </p>
          </div>
        </div>

        {/* Patient Selector */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Selecionar Paciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar paciente por nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-paciente"
                />
              </div>
              {selectedInternacao && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <User className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">{selectedInternacao.paciente_nome}</span>
                  <Badge variant="outline" className="ml-2">
                    {selectedInternacao.origem || 'N/A'}
                  </Badge>
                </div>
              )}
            </div>
            
            {searchQuery && filteredInternacoes.length > 0 && (
              <div className="mt-3 border rounded-lg max-h-48 overflow-y-auto">
                {filteredInternacoes.slice(0, 10).map((int) => (
                  <div
                    key={int.id}
                    className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0 flex items-center justify-between"
                    onClick={() => handleSelectInternacao(int)}
                    data-testid={`select-internacao-${int.id}`}
                  >
                    <div>
                      <p className="font-medium">{int.paciente_nome}</p>
                      <p className="text-sm text-muted-foreground">
                        {int.origem} • Internado: {int.data_internacao}
                      </p>
                    </div>
                    <Badge>{int.status_kanban || 'ativo'}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Overdue Alerts */}
        {alertasVencidas.length > 0 && (
          <Card className="border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-800 dark:text-orange-200">
                    Alertas Clínicos
                  </p>
                  <ul className="mt-1 space-y-1">
                    {alertasVencidas.map((alerta, idx) => (
                      <li key={idx} className="text-sm text-orange-700 dark:text-orange-300">
                        {alerta.mensagem}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content - Tabs */}
        {selectedInternacao ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
              <TabsTrigger value="medica" className="flex items-center gap-2" data-testid="tab-medica">
                <Stethoscope className="h-4 w-4" />
                Médica
              </TabsTrigger>
              <TabsTrigger value="enfermagem" className="flex items-center gap-2" data-testid="tab-enfermagem">
                <Heart className="h-4 w-4" />
                Enfermagem
              </TabsTrigger>
            </TabsList>

            {/* MEDICAL TAB */}
            <TabsContent value="medica" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Stethoscope className="h-5 w-5 text-blue-600" />
                        Prescrição Médica
                      </CardTitle>
                      <CardDescription>
                        {prescricaoMedica 
                          ? `Última atualização: ${formatDateTime(prescricaoMedica.created_at)} por ${prescricaoMedica.created_by_nome || 'Sistema'}`
                          : 'Nenhuma prescrição ativa'
                        }
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHistoricoMedica(!showHistoricoMedica)}
                      >
                        <History className="h-4 w-4 mr-1" />
                        Histórico
                      </Button>
                      {historicoPrescricoesMedicas.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyPrescricao(historicoPrescricoesMedicas[0].id, 'medica')}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copiar Anterior
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* History Panel */}
                  {showHistoricoMedica && (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <h4 className="font-medium mb-3">Histórico de Prescrições</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {historicoPrescricoesMedicas.map((presc, idx) => (
                          <div
                            key={presc.id}
                            className="flex items-center justify-between p-2 bg-background rounded border"
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant={presc.status === 'Ativa' ? 'default' : 'secondary'}>
                                {presc.status}
                              </Badge>
                              <span className="text-sm">
                                {formatDateTime(presc.created_at)}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                por {presc.created_by_nome || 'Sistema'}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyPrescricao(presc.id, 'medica')}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Services/Items Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-medium">Serviços e Itens</Label>
                      <Button
                        size="sm"
                        onClick={() => setShowAddItemModal(true)}
                        disabled={!canEditMedica}
                        data-testid="add-item-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Item
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {formMedica.itens.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 border rounded-lg bg-background"
                        >
                          <div className="flex-shrink-0">
                            {item.tipo === 'Servico' && <Briefcase className="h-4 w-4 text-green-600" />}
                            {item.tipo === 'MedicamentoEstoque' && <Pill className="h-4 w-4 text-blue-600" />}
                            {item.tipo === 'MedicamentoNaoCadastrado' && <AlertCircle className="h-4 w-4 text-orange-600" />}
                            {item.tipo === 'MaterialEstoque' && <Package className="h-4 w-4 text-purple-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {item.nome_ref || item.nome_livre || 'Item'}
                              {item.tipo === 'MedicamentoNaoCadastrado' && (
                                <Badge variant="outline" className="ml-2 text-orange-600">
                                  Fora do estoque
                                </Badge>
                              )}
                            </p>
                            {item.dose && (
                              <p className="text-sm text-muted-foreground">
                                Dose: {item.dose} {item.unidade}
                              </p>
                            )}
                          </div>
                          <Select
                            value={item.frequencia}
                            onValueChange={(val) => handleUpdateItemFrequencia(idx, val)}
                            disabled={!canEditMedica}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FREQUENCIA_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {canEditMedica && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(idx)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ))}
                      
                      {formMedica.itens.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          Nenhum item adicionado
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Orientações */}
                  <div>
                    <Label className="text-base font-medium">Orientações</Label>
                    <Textarea
                      value={formMedica.orientacoes}
                      onChange={(e) => setFormMedica(prev => ({ ...prev, orientacoes: e.target.value }))}
                      placeholder="Orientações gerais para o paciente..."
                      className="mt-2 min-h-[100px]"
                      disabled={!canEditMedica}
                      data-testid="orientacoes-textarea"
                    />
                  </div>

                  {/* Evolução */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-base font-medium">Evolução</Label>
                      {evolucoesMedicas.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyEvolucao(evolucoesMedicas[0].id, 'Medica')}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copiar Anterior
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={formMedica.evolucao}
                      onChange={(e) => setFormMedica(prev => ({ ...prev, evolucao: e.target.value }))}
                      placeholder="Registro de evolução clínica..."
                      className="min-h-[150px]"
                      disabled={!canEditMedica}
                      data-testid="evolucao-medica-textarea"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 inline mr-1 text-green-600" />
                      Ao salvar, medicamentos e serviços serão adicionados automaticamente ao Consumo e Serviços
                    </p>
                    <Button
                      onClick={handleSaveMedica}
                      disabled={loading || !canEditMedica}
                      data-testid="save-medica-btn"
                    >
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Salvar Prescrição
                    </Button>
                  </div>

                  {!canEditMedica && (
                    <p className="text-sm text-muted-foreground text-center py-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      Apenas médicos e administradores podem editar prescrições médicas
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* NURSING TAB */}
            <TabsContent value="enfermagem" className="space-y-4">
              {/* Reference to Medical Prescription */}
              {prescricaoMedica && (
                <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Prescrição Médica Ativa (Referência)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm space-y-2">
                      <p className="text-muted-foreground">
                        Atualizada em {formatDateTime(prescricaoMedica.created_at)}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {prescricaoMedica.itens?.slice(0, 5).map((item, idx) => (
                          <Badge key={idx} variant="outline">
                            {item.nome_ref || item.nome_livre}
                          </Badge>
                        ))}
                        {prescricaoMedica.itens?.length > 5 && (
                          <Badge variant="secondary">+{prescricaoMedica.itens.length - 5}</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-pink-600" />
                        Prescrição de Enfermagem
                      </CardTitle>
                      <CardDescription>
                        {prescricaoEnfermagem 
                          ? `Última atualização: ${formatDateTime(prescricaoEnfermagem.created_at)} por ${prescricaoEnfermagem.created_by_nome || 'Sistema'}`
                          : 'Nenhuma prescrição ativa'
                        }
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHistoricoEnfermagem(!showHistoricoEnfermagem)}
                      >
                        <History className="h-4 w-4 mr-1" />
                        Histórico
                      </Button>
                      {historicoPrescricoesEnfermagem.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyPrescricao(historicoPrescricoesEnfermagem[0].id, 'enfermagem')}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copiar Anterior
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* History Panel */}
                  {showHistoricoEnfermagem && (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <h4 className="font-medium mb-3">Histórico de Prescrições</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {historicoPrescricoesEnfermagem.map((presc, idx) => (
                          <div
                            key={presc.id}
                            className="flex items-center justify-between p-2 bg-background rounded border"
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant={presc.status === 'Ativa' ? 'default' : 'secondary'}>
                                {presc.status}
                              </Badge>
                              <span className="text-sm">
                                {formatDateTime(presc.created_at)}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyPrescricao(presc.id, 'enfermagem')}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Materials Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-medium">Materiais Planejados</Label>
                      <Button
                        size="sm"
                        onClick={() => setShowAddMaterialModal(true)}
                        disabled={!canEditEnfermagem}
                        data-testid="add-material-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Material
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {formEnfermagem.materiais.map((mat, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 border rounded-lg bg-background"
                        >
                          <Package className="h-4 w-4 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{mat.nome || 'Material'}</p>
                            <p className="text-sm text-muted-foreground">
                              {mat.quantidade_por_uso} {mat.unidade} por uso
                              {mat.recorrente && ' • Recorrente'}
                              {mat.por_diaria && ' • Por diária'}
                            </p>
                          </div>
                          <Badge variant="outline">{mat.frequencia}</Badge>
                          {canEditEnfermagem && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveMaterial(idx)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ))}
                      
                      {formEnfermagem.materiais.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          Nenhum material planejado
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Evolução */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-base font-medium">Evolução</Label>
                      {evolucoesEnfermagem.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyEvolucao(evolucoesEnfermagem[0].id, 'Enfermagem')}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copiar Anterior
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={formEnfermagem.evolucao}
                      onChange={(e) => setFormEnfermagem(prev => ({ ...prev, evolucao: e.target.value }))}
                      placeholder="Registro de evolução de enfermagem..."
                      className="min-h-[150px]"
                      disabled={!canEditEnfermagem}
                      data-testid="evolucao-enfermagem-textarea"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 inline mr-1 text-green-600" />
                      Ao salvar, materiais serão adicionados automaticamente ao Consumo
                    </p>
                    <Button
                      onClick={handleSaveEnfermagem}
                      disabled={loading || !canEditEnfermagem}
                      data-testid="save-enfermagem-btn"
                    >
                      {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Salvar Prescrição
                    </Button>
                  </div>

                  {!canEditEnfermagem && (
                    <p className="text-sm text-muted-foreground text-center py-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                      Apenas enfermeiros e administradores podem editar prescrições de enfermagem
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">Selecione um paciente</p>
              <p className="text-muted-foreground">
                Use a busca acima para selecionar um paciente e gerenciar suas prescrições
              </p>
            </CardContent>
          </Card>
        )}

        {/* Add Item Modal */}
        <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Adicionar Item</DialogTitle>
              <DialogDescription>
                Adicione medicamentos, materiais ou serviços à prescrição
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={newItem.tipo}
                  onValueChange={(val) => setNewItem(prev => ({ ...prev, tipo: val, ref_id: '', nome_livre: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MedicamentoEstoque">Medicamento (Estoque)</SelectItem>
                    <SelectItem value="MedicamentoNaoCadastrado">Medicamento (Não cadastrado)</SelectItem>
                    <SelectItem value="MaterialEstoque">Material (Estoque)</SelectItem>
                    <SelectItem value="Servico">Serviço</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newItem.tipo === 'MedicamentoNaoCadastrado' ? (
                <div>
                  <Label>Nome do Medicamento</Label>
                  <Input
                    value={newItem.nome_livre}
                    onChange={(e) => setNewItem(prev => ({ ...prev, nome_livre: e.target.value }))}
                    placeholder="Digite o nome do medicamento"
                  />
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Um alerta será enviado aos administradores
                  </p>
                </div>
              ) : newItem.tipo === 'Servico' ? (
                <div>
                  <Label>Serviço</Label>
                  <Select
                    value={newItem.ref_id}
                    onValueChange={(val) => setNewItem(prev => ({ ...prev, ref_id: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um serviço" />
                    </SelectTrigger>
                    <SelectContent>
                      {servicosCatalogo.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Item do Estoque</Label>
                  <Combobox
                    value={newItem.ref_id}
                    onValueChange={(val) => setNewItem(prev => ({ ...prev, ref_id: val }))}
                    placeholder="Digite para buscar..."
                    searchPlaceholder="Buscar medicamento ou material..."
                    emptyText="Nenhum item encontrado"
                    allowCreate={true}
                    createNewLabel="Adicionar item não cadastrado"
                    onCreateNew={(searchTerm) => {
                      setNonStockItemName(searchTerm);
                      setShowCreateNonStockModal(true);
                    }}
                    options={estoqueItens
                      .filter(e => newItem.tipo === 'MedicamentoEstoque' 
                        ? e.tipo === 'medicamento' 
                        : e.tipo === 'material')
                      .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
                      .map((e) => ({
                        value: e.id,
                        label: e.nome,
                        subtitle: `${e.estoque_atual || 0} em estoque`,
                        badge: e.tipo === 'medicamento' ? 'Med' : 'Mat'
                      }))}
                    data-testid="item-estoque-combobox"
                  />
                </div>
              )}

              {(newItem.tipo === 'MedicamentoEstoque' || newItem.tipo === 'MedicamentoNaoCadastrado') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Dose</Label>
                    <Input
                      value={newItem.dose}
                      onChange={(e) => setNewItem(prev => ({ ...prev, dose: e.target.value }))}
                      placeholder="Ex: 500"
                    />
                  </div>
                  <div>
                    <Label>Unidade</Label>
                    <Input
                      value={newItem.unidade}
                      onChange={(e) => setNewItem(prev => ({ ...prev, unidade: e.target.value }))}
                      placeholder="Ex: mg"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>Frequência</Label>
                <Select
                  value={newItem.frequencia}
                  onValueChange={(val) => setNewItem(prev => ({ ...prev, frequencia: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIA_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Observações</Label>
                <Input
                  value={newItem.observacoes}
                  onChange={(e) => setNewItem(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Observações opcionais"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddItemModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddItem} data-testid="confirm-add-item">
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Material Modal */}
        <Dialog open={showAddMaterialModal} onOpenChange={setShowAddMaterialModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Adicionar Material</DialogTitle>
              <DialogDescription>
                Planeje o uso de materiais do estoque
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Material do Estoque</Label>
                <Combobox
                  value={newMaterial.estoque_item_id}
                  onValueChange={(val) => setNewMaterial(prev => ({ ...prev, estoque_item_id: val }))}
                  placeholder="Digite para buscar material..."
                  searchPlaceholder="Buscar material..."
                  emptyText="Nenhum material encontrado"
                  allowCreate={true}
                  createNewLabel="Adicionar material não cadastrado"
                  onCreateNew={(searchTerm) => {
                    setNonStockItemName(searchTerm);
                    setShowCreateNonStockModal(true);
                  }}
                  options={estoqueItens
                    .filter(e => e.tipo === 'material')
                    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
                    .map((e) => ({
                      value: e.id,
                      label: e.nome,
                      subtitle: `${e.estoque_atual || 0} em estoque`
                    }))}
                  data-testid="material-estoque-combobox"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantidade por Uso</Label>
                  <Input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={newMaterial.quantidade_por_uso}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, quantidade_por_uso: parseFloat(e.target.value) || 1 }))}
                  />
                </div>
                <div>
                  <Label>Unidade</Label>
                  <Input
                    value={newMaterial.unidade}
                    onChange={(e) => setNewMaterial(prev => ({ ...prev, unidade: e.target.value }))}
                    placeholder="Ex: un, ml"
                  />
                </div>
              </div>

              <div>
                <Label>Frequência</Label>
                <Select
                  value={newMaterial.frequencia}
                  onValueChange={(val) => setNewMaterial(prev => ({ ...prev, frequencia: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIA_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newMaterial.recorrente}
                    onCheckedChange={(val) => setNewMaterial(prev => ({ ...prev, recorrente: val }))}
                  />
                  <Label>Recorrente</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newMaterial.por_diaria}
                    onCheckedChange={(val) => setNewMaterial(prev => ({ ...prev, por_diaria: val }))}
                  />
                  <Label>Por diária</Label>
                </div>
              </div>

              <div>
                <Label>Observações</Label>
                <Input
                  value={newMaterial.observacoes}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Observações opcionais"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddMaterialModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddMaterial} data-testid="confirm-add-material">
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Create Non-Stock Item */}
        <Dialog open={showCreateNonStockModal} onOpenChange={setShowCreateNonStockModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                Item não cadastrado
              </DialogTitle>
              <DialogDescription>
                O item "{nonStockItemName}" não foi encontrado no estoque. Deseja adicioná-lo como item não cadastrado?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Atenção:</strong> Um alerta será enviado aos administradores para cadastrar este item no estoque.
                </p>
              </div>
              <div>
                <Label>Nome do Item</Label>
                <Input
                  value={nonStockItemName}
                  onChange={(e) => setNonStockItemName(e.target.value)}
                  placeholder="Nome do medicamento ou material"
                  data-testid="non-stock-item-name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateNonStockModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={async () => {
                  if (!nonStockItemName.trim()) {
                    toast.error('Digite o nome do item');
                    return;
                  }
                  
                  // Add as non-stock item to the current prescription
                  setNewItem(prev => ({
                    ...prev,
                    tipo: 'MedicamentoNaoCadastrado',
                    nome_livre: nonStockItemName.trim(),
                    ref_id: ''
                  }));
                  
                  // Send alert to admins
                  try {
                    await api.post('/notificacoes', {
                      tipo: 'item_nao_cadastrado',
                      titulo: 'Item não encontrado no estoque',
                      mensagem: `O item "${nonStockItemName.trim()}" foi prescrito mas não está cadastrado no estoque. Por favor, cadastre-o para permitir controle de estoque adequado.`,
                      prioridade: 'alta',
                      target_roles: ['admin', 'master_admin']
                    });
                    toast.success('Administradores notificados sobre o item não cadastrado');
                  } catch (error) {
                    console.error('Error sending notification:', error);
                    // Don't block - notification is secondary
                  }
                  
                  setShowCreateNonStockModal(false);
                  toast.info(`"${nonStockItemName}" adicionado como item não cadastrado`);
                }}
                data-testid="confirm-non-stock-item"
              >
                Confirmar e Notificar Admin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Prescricao;
